import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  Storage as StorageIcon,
  ShoppingCart as ShoppingCartIcon,
  Schedule as ScheduleIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useProductCacheStats } from "../../../hooks/useProductCacheAdmin";

const formatTimeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return `${seconds} second${seconds > 1 ? 's' : ''} ago`;
};

const ProductCacheStats: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useProductCacheStats();

  const stats = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t("admin.productCache.noStatisticsAvailable")}
      </Typography>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <StorageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.productCache.totalProducts")}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.totalProducts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <DeleteSweepIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.productCache.expiredLabel")}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.expiredProducts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ScheduleIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.productCache.oldestCache")}
                </Typography>
              </Box>
              <Typography variant="body1">
                {stats.oldestCachedAt
                  ? formatTimeAgo(stats.oldestCachedAt)
                  : "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ScheduleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.productCache.newestCache")}
                </Typography>
              </Box>
              <Typography variant="body1">
                {stats.newestCachedAt
                  ? formatTimeAgo(stats.newestCachedAt)
                  : "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("admin.productCache.productsByRetailer")}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                {Object.entries(stats.productsByRetailer || {}).map(
                  ([retailer, count]) => (
                    <Card key={retailer} sx={{ minWidth: 200 }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          {retailer.toUpperCase()}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                          {(count as number).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  )
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductCacheStats;

